// Firebase email/password auth context.
//
// Backed by the `alphaquark-59f50` Firebase project (auth domain
// auth.alphaquark.in) — same project as the rest of the AlphaQuark fleet,
// config read from VITE_FIREBASE_* env only (see src/lib/firebase.js).
//
// Shape the rest of the app depends on:
//   useAuth() -> { user, loading, configured, signIn, signUp,
//                  resetPassword, logout }
//   getIdToken() -> module-level export apiClient.js calls (outside React)
//     to attach the Bearer token on authenticated requests.

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  updateProfile,
} from 'firebase/auth';

import { auth, isFirebaseConfigured } from '../lib/firebase.js';

// Module-level provider so redirect sign-in can resume across the full-page
// navigation back from Google. Redirect (not popup) is deliberate: popup's
// cross-origin handoff to <project>.firebaseapp.com fails in modern Chrome
// (storage partitioning), Brave shields, and Safari ITP — redirect uses
// IndexedDB on the app's own origin and works everywhere (matches the rest
// of the AlphaQuark fleet).
const googleProvider = new GoogleAuthProvider();

const AuthContext = createContext(null);

// Module-level so apiClient.js (outside React) can read the current token
// without needing a hook. Kept in sync by the effect below.
let currentIdTokenGetter = async () => null;

export async function getIdToken(opts) {
  return currentIdTokenGetter(opts);
}

// Firebase error codes -> friendly copy. Anything not in this map falls
// back to err.message (still better than a raw stack in a form).
const ERROR_MESSAGES = {
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/invalid-email': "That email address doesn't look right.",
  'auth/user-not-found': 'No account found for that email.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/email-already-in-use': 'An account with that email already exists — try signing in instead.',
  'auth/weak-password': 'Choose a password with at least 6 characters.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/network-request-failed': 'Network error — check your connection and try again.',
  'auth/user-disabled': 'This account has been disabled. Contact support.',
  'auth/missing-password': 'Enter a password.',
};

function friendlyMessage(err) {
  const code = err && err.code;
  return (code && ERROR_MESSAGES[code]) || (err && err.message) || 'Something went wrong. Please try again.';
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isFirebaseConfigured || !auth) {
      setLoading(false);
      return undefined;
    }
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    // If we just came back from the Google redirect, firebase/auth restores
    // the session via onAuthStateChanged — this resolves any error that
    // occurred during the handoff so the user gets a friendly message
    // instead of a silent hang. No-op on normal loads.
    getRedirectResult(auth).catch(() => {
      /* errors surfaced by the next sign-in attempt */
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    currentIdTokenGetter = async (opts) => {
      if (!isFirebaseConfigured || !auth?.currentUser) return null;
      try {
        // forceRefresh: re-issue the ID token (expired-token recovery on 401).
        return await auth.currentUser.getIdToken(opts?.forceRefresh || false);
      } catch {
        return null;
      }
    };
    return () => {
      currentIdTokenGetter = async () => null;
    };
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      loading,
      // `configured` is the current name; `isConfigured` kept as an alias
      // for anything written against the earlier stub's naming.
      configured: isFirebaseConfigured,
      isConfigured: isFirebaseConfigured,

      async signIn(email, password) {
        if (!isFirebaseConfigured || !auth) {
          throw new Error("Sign-in isn't configured on this deployment yet.");
        }
        try {
          const cred = await signInWithEmailAndPassword(auth, email, password);
          return cred.user;
        } catch (err) {
          throw new Error(friendlyMessage(err));
        }
      },

      // Google sign-in via redirect. Navigates away to Google and back; the
      // mount-effect getRedirectResult + onAuthStateChanged resume the session.
      // Returns true when the redirect was initiated (the browser is leaving).
      async signInWithGoogle() {
        if (!isFirebaseConfigured || !auth) {
          throw new Error("Sign-in isn't configured on this deployment yet.");
        }
        await signInWithRedirect(auth, googleProvider);
        return true;
      },

      async signUp(email, password, displayName) {
        if (!isFirebaseConfigured || !auth) {
          throw new Error("Sign-up isn't configured on this deployment yet.");
        }
        try {
          const cred = await createUserWithEmailAndPassword(auth, email, password);
          if (displayName) {
            await updateProfile(cred.user, { displayName });
          }
          return cred.user;
        } catch (err) {
          throw new Error(friendlyMessage(err));
        }
      },

      async resetPassword(email) {
        if (!isFirebaseConfigured || !auth) {
          throw new Error("Password reset isn't configured on this deployment yet.");
        }
        try {
          await sendPasswordResetEmail(auth, email);
        } catch (err) {
          throw new Error(friendlyMessage(err));
        }
      },

      async logout() {
        if (!isFirebaseConfigured || !auth) {
          setUser(null);
          return;
        }
        await firebaseSignOut(auth);
      },

      // Back-compat aliases for the earlier stub's method names.
      async signOut() {
        if (!isFirebaseConfigured || !auth) {
          setUser(null);
          return;
        }
        await firebaseSignOut(auth);
      },
      async signInWithEmail(email, password) {
        if (!isFirebaseConfigured || !auth) {
          throw new Error("Sign-in isn't configured on this deployment yet.");
        }
        try {
          const cred = await signInWithEmailAndPassword(auth, email, password);
          return cred.user;
        } catch (err) {
          throw new Error(friendlyMessage(err));
        }
      },
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
